import { Link } from "react-router";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { Reveal } from "../components/motion";
import { SectionShell, isDarkBackground, type SectionProps } from "./SectionRenderer";

export default function ConsultationCtaBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["consultationCta"]>) {
  const onDark = isDarkBackground(settings);

  return (
    <SectionShell settings={settings}>
      <Reveal
        className={cn(
          content.layout === "card" && "rounded-2xl bg-navy-900 px-8 py-14 text-center md:px-16",
          content.layout === "banner" && "text-center",
        )}
      >
        {content.eyebrow && (
          <p className="text-xs font-semibold uppercase tracking-wider text-gold-300">
            {content.eyebrow}
          </p>
        )}

        <h2
          className={cn(
            "mt-3 text-4xl",
            onDark || content.layout === "card" ? "text-cream-50" : "text-navy-900",
          )}
        >
          {content.heading}
        </h2>

        {content.body && (
          <p
            className={cn(
              "mx-auto mt-4 max-w-2xl text-lg leading-relaxed",
              onDark || content.layout === "card" ? "text-navy-100" : "text-ink-600",
            )}
          >
            {content.body}
          </p>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to={content.primaryButton.href}
            className="rounded-md bg-gold-500 px-7 py-3.5 text-sm font-semibold text-navy-900 transition-colors hover:bg-gold-400"
          >
            {content.primaryButton.label}
          </Link>
          {content.secondaryButton?.href && content.secondaryButton.label && (
            <Link
              to={content.secondaryButton.href}
              className={cn(
                "rounded-md px-7 py-3.5 text-sm font-semibold ring-1 ring-inset transition-colors",
                onDark || content.layout === "card"
                  ? "text-cream-100 ring-cream-100/30 hover:bg-cream-100/10"
                  : "text-navy-900 ring-ink-300 hover:bg-ink-50",
              )}
            >
              {content.secondaryButton.label}
            </Link>
          )}
        </div>
      </Reveal>
    </SectionShell>
  );
}
